import Client from '../../../../model/api/Client';
import React, { LegacyRef, useCallback, useEffect, useRef, useState } from 'react';
import { SuperSelect, SuperSelectDivider } from '../../../superSelect/SuperSelect';
import { Stars } from './Stars';
import validator from 'validator';
import { webalize } from '../../../../../utils';
import { sort } from '../../../../model/helper/benefits/Sort';
import { TextArea } from '../../../form/TextArea';
import { Submit } from '../../../form/Submit';

interface IProps {
	slug: string;
	apiClient: Client;
	email: string;
	codexUrl: string;
}

interface IValues {
	slug: string;
	email: string;
	actualJob: boolean;
	ratingTextPositive: string;
	ratingTextNegative: string;
	ratingTextImprovement: string;
	benefits: Array<number>;
	recommended: null | number;
	localityId: null | number;
	departmentId: null | number;
	departmentName: string;
	ratings: Array<{ id: number; val: number }>;
	gdprAgreement: boolean;
}

interface IItem {
	id: number;
	name: string;
	webalizedName?: string;
	isDefault?: boolean;
}

interface IRatingGroup {
	name: string;
	items: Array<IItem>;
}

interface IErrors {
	locality: boolean;
	ratings: boolean;
	recommended: boolean;
	department: boolean;
	email: boolean;
}

const hasErrors = (errorsObject: IErrors) => {
	for (const [, value] of Object.entries(errorsObject)) {
		if (value) {
			return true;
		}
	}

	return false;
};

export const Step2 = ({ slug, apiClient, email, codexUrl }: IProps) => {
	const initFormValues = {
		email: email,
		slug: slug,
		actualJob: true,
		ratingTextPositive: '',
		ratingTextNegative: '',
		ratingTextImprovement: '',
		benefits: [],
		recommended: null,
		localityId: null,
		departmentId: null,
		departmentName: '',
		ratings: [],
		gdprAgreement: true
	};
	const [formValues, setFormValues] = useState<IValues>(initFormValues);
	const initValues = useRef(initFormValues);

	const [benefits, setBenefits] = useState<Array<IItem>>([]);
	const [departments, setDepartments] = useState<Array<IItem>>([]);
	const [ratingData, setRatingData] = useState<Array<IRatingGroup>>([]);
	const [errors, setErrors] = useState<IErrors>({
		locality: false,
		ratings: false,
		recommended: false,
		department: false,
		email: false
	});
	const [disableLeaveConfirm, setDisableLeaveConfirm] = useState<boolean>(false);
	const localityRef = useRef();
	const departmentRef = useRef();
	const ratingsRef = useRef();
	const recommendedRef = useRef();
	const [forceLocalityError, setForceLocalityError] = useState(false);
	const [forceDepartmentError, setForceDepartmentError] = useState(false);
	const [showAllBenefits, setShowAllBenefits] = useState(false);
	const [isApiError, setIsApiError] = useState(false);

	const getRatingItemsCount = useCallback(() => {
		let ratingItemsCount: number = 0;
		ratingData.map((group: IRatingGroup) => (ratingItemsCount += group.items.length));

		return ratingItemsCount;
	}, [ratingData]);

	const hasFormChanged = useCallback(() => {
		return JSON.stringify(formValues) !== JSON.stringify(initValues.current);
	}, [formValues, initValues]);

	useEffect(() => {
		// Slouzi pro hlasku - opravdu chcete odejit z teto stranky ?
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (disableLeaveConfirm) {
				return;
			}
			if (hasFormChanged()) {
				event.preventDefault();
				event.returnValue = '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	});

	const validateForm = useCallback(() => {
		const newErrors: IErrors = {
			locality: false,
			ratings: false,
			recommended: false,
			department: false,
			email: false
		};

		if (formValues.ratings.length < getRatingItemsCount()) {
			newErrors.ratings = true;
		}

		if (formValues.localityId === null) {
			newErrors.locality = true;
		}

		if (formValues.recommended === null) {
			newErrors.recommended = true;
		}

		if (formValues.departmentName === '' || formValues.departmentName === null) {
			newErrors.department = true;
		}

		if (!validator.isEmail(formValues.email)) {
			newErrors.email = true;
		}

		if (JSON.stringify(newErrors) === JSON.stringify(errors)) {
			return hasErrors(newErrors);
		}

		setErrors(newErrors);

		return hasErrors(newErrors);
	}, [formValues, setErrors, getRatingItemsCount, errors]);

	useEffect(() => {
		if (errors.locality && localityRef.current) {
			(localityRef.current as HTMLElement).scrollIntoView();
		} else if (errors.department && departmentRef.current) {
			(departmentRef.current as HTMLElement).scrollIntoView();
		} else if (errors.ratings && ratingsRef.current) {
			(ratingsRef.current as HTMLElement).scrollIntoView();
		} else if (errors.recommended && recommendedRef.current) {
			(recommendedRef.current as HTMLElement).scrollIntoView();
		}
	}, [errors, localityRef, ratingsRef, departmentRef, recommendedRef]);

	const getListFromResponse = (list: any): Array<IItem> => {
		const preferredItems: Array<IItem> = [];
		const otherItems: Array<IItem> = [];
		for (const key in list) {
			const item = list[key];
			const isDefault = isNaN(item.id); // if ID is not a number then it means, value is from default tables offer_job_subject_profession/offer_job_subject
			if (item.preferred) {
				preferredItems.push({
					id: parseInt(item.id),
					name: item.value,
					webalizedName: webalize(item.value),
					isDefault: isDefault
				});
			} else {
				otherItems.push({
					id: parseInt(item.id),
					name: item.value,
					webalizedName: webalize(item.value),
					isDefault: isDefault
				});
			}
		}

		if (preferredItems.length > 0) {
			preferredItems.push({
				id: -1,
				name: 'divider_tag',
				isDefault: false
			});
		}

		return preferredItems.concat(otherItems);
	};

	useEffect(() => {
		apiClient
			.benefitList()
			.then((response) => {
				if (response === null) {
					return;
				}

				const data: Array<IItem> = [];
				for (const id in response) {
					let name = response[id].name;

					if (name === 'Pracovní zařízení (mobil, PC, notebook...)') {
						name = 'Pracovní zařízení';
					}

					data.push({
						id: parseInt(response[id].id),
						name: name
					});
				}

				function compare(a: IItem, b: IItem) {
					if (a.name < b.name) {
						return -1;
					}
					if (a.name > b.name) {
						return 1;
					}
					return 0;
				}

				data.sort(compare);

				setBenefits(sort(data));
			})
			.catch(() => {
				setIsApiError(true);
			});

		apiClient
			.departmentList(slug)
			.then((response) => {
				if (response === null) {
					return;
				}

				setDepartments(getListFromResponse(response.list));
			})
			.catch(() => {
				setIsApiError(true);
			});

		apiClient
			.ratingDataList()
			.then((response) => {
				if (response === null) {
					return;
				}

				setRatingData(Object.values(response.result));
			})
			.catch(() => {
				setIsApiError(true);
			});
	}, [apiClient, setBenefits, setDepartments, setRatingData, slug, setIsApiError]);

	const onChange = useCallback(
		(e: any) => {
			let { name, value } = e.target;

			if (name === 'rating') {
				const id = e.target.id;

				let ratings = [...formValues.ratings];
				let ratingExist = false;
				ratings = ratings.map((item) => {
					if (item.id === id) {
						item.val = value;
						ratingExist = true;
					}

					return item;
				});

				if (!ratingExist) {
					ratings.push({
						id,
						val: value
					});
				}

				name = 'ratings';
				value = ratings;
			}

			const newFormValues = { ...formValues, [name]: value };

			setFormValues(newFormValues);
		},
		[formValues, setFormValues]
	);

	const onTextChange = useCallback(
		(inputName: string, value: string) => {
			const newFormValues = { ...formValues, [inputName]: value };

			setFormValues(newFormValues);
		},
		[formValues, setFormValues]
	);

	useEffect(() => {
		if (hasErrors(errors)) {
			validateForm();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formValues, errors]);

	const addBenefit = useCallback(
		(id: number) => {
			const newFormValues = { ...formValues };

			newFormValues.benefits.push(id);

			setFormValues(newFormValues);
		},
		[formValues]
	);

	const removeBenefit = useCallback(
		(id: number) => {
			const newFormValues = { ...formValues };

			newFormValues.benefits = newFormValues.benefits.filter((benefitId: number) => benefitId !== id);

			setFormValues(newFormValues);
		},
		[formValues]
	);

	const locationOnSearch = useCallback(
		(search: string, setList: (list: Array<any>) => void) => {
			setForceLocalityError(true);
			onChange({
				target: {
					name: 'localityId',
					value: null
				}
			});
			if (search === '') {
				setList([]);
			} else {
				const handleLocationResponse = (response: any) =>
					response !== null ? setList(response.results.map((item: any) => item)) : null;

				apiClient.locationsSearch(search).then(handleLocationResponse);
			}
		},
		[apiClient, onChange, setForceLocalityError]
	);

	const departmentOnSearch = useCallback(
		(search: string, setList: (list: Array<any>) => void) => {
			onChange({
				target: {
					name: 'departmentId',
					value: null
				}
			});

			onChange({
				target: {
					name: 'departmentName',
					value: null
				}
			});

			setForceDepartmentError(true);
			let newList;
			if (search === '') {
				newList = departments;
			} else {
				let searchedTerm = webalize(search);

				const onFilter = (item: IItem) =>
					item.webalizedName !== undefined &&
					item.webalizedName.includes(searchedTerm) &&
					item.webalizedName !== 'divider_tag'; // Filtering directly with preprocessed names

				const onSort = (a: IItem, b: IItem) => {
					if (a.webalizedName === undefined || b.webalizedName === undefined) {
						return 0;
					}
					const aStartsWith = a.webalizedName.startsWith(searchedTerm);
					const bStartsWith = b.webalizedName.startsWith(searchedTerm);

					if (aStartsWith && !bStartsWith) {
						return -1;
					}
					if (bStartsWith && !aStartsWith) {
						return 1;
					}
					return 0;
				};

				newList = departments.filter(onFilter).sort(onSort);
			}

			setList(newList);
		},
		[departments, setForceDepartmentError, onChange]
	);

	const getRatingValue = (id: number): number => {
		for (let i in formValues.ratings) {
			const item = formValues.ratings[i];

			if (item.id === id) {
				return item.val;
			}
		}

		return 0;
	};

	const getAverageRatingValue = (returnInt: boolean = true): number => {
		if (formValues.ratings.length === 0) {
			return 0;
		}

		let value = 0;
		formValues.ratings.map((item: any) => (value += item.val));

		if (returnInt) {
			return Math.round(value / formValues.ratings.length);
		}

		return parseFloat((value / formValues.ratings.length).toFixed(1));
	};

	const sendForm = useCallback(
		(callback: () => void) => {
			if (validateForm()) {
				callback();
				return;
			}

			setDisableLeaveConfirm(true);

			apiClient.addReview(formValues).then((response: any) => {
				if (response.result.redirectUrl !== undefined) {
					window.location.replace(response.result.redirectUrl);
				}
			});
		},
		[validateForm, apiClient, formValues, setDisableLeaveConfirm]
	);

	const isLocalityError = useCallback(() => {
		return errors.locality || (forceLocalityError && formValues.localityId === null);
	}, [errors, forceLocalityError, formValues.localityId]);

	const isDepartmentError = useCallback(() => {
		return errors.department || (forceDepartmentError && formValues.departmentId === null);
	}, [errors, forceDepartmentError, formValues.departmentId]);

	const onShowMore = useCallback(() => setShowAllBenefits(true), []);
	const renderBenefits = useCallback(() => {
		const benefitsArray = showAllBenefits ? benefits : benefits.slice(0, 12);
		return (
			<>
				{benefitsArray.map((item: IItem, key) => (
					<div
						className="col-12 col-md-4"
						key={key}
						/* eslint-disable-next-line react/jsx-no-bind */
						onClick={() => {
							formValues.benefits.includes(item.id) ? removeBenefit(item.id) : addBenefit(item.id);
						}}>
						<div className="benefit-item mb-2 me-2 py-2 px-2 border rounded d-flex flex-nowrap align-items-center">
							<input
								className="form-check-input mt-0 me-2"
								type="checkbox"
								checked={formValues.benefits.includes(item.id)}
								readOnly={true}
							/>
							<label className="form-check-label mb-0">{item.name}</label>
						</div>
					</div>
				))}
				{showAllBenefits ? null : (
					<a className={'mt-2'} style={{ cursor: 'pointer' }} onClick={onShowMore}>
						Zobrazit všechny benefity
					</a>
				)}
			</>
		);
	}, [benefits, showAllBenefits, onShowMore, addBenefit, formValues.benefits, removeBenefit]);

	return (
		<>
			<section className="top-form">
				<div className="container-lg container-lg-max">
					<div className="mt-5 mt-md-7 mb-4">
						{isApiError ? (
							<div className="alert alert-danger">
								Nastala chyba při načítání dat.{' '}
								<a
									href="#"
									/* eslint-disable-next-line react/jsx-no-bind */
									onClick={() => window.location.reload()}>
									Zkusit znovu
								</a>
								.
							</div>
						) : null}
						<h3>Základní informace</h3>
						<div className="form-row mt-5">
							<div className="col-12 col-md-6 form-group required">
								<label className="h5 control-label">Co chci hodnotit</label>
								<div className="d-flex flex-wrap gap-3">
									<div
										className={
											'border border-1 rounded px-3 me-3 d-inline-block radio-cont' +
											(formValues.actualJob ? ' highlight-border' : '')
										}
										/* eslint-disable-next-line react/jsx-no-bind */
										onClick={() =>
											onChange({
												target: {
													name: 'actualJob',
													value: true
												}
											})
										}>
										<input
											type="radio"
											checked={formValues.actualJob}
											className="form-check-input me-1"
											readOnly={true}
										/>
										<label className="control-label">Aktuální zaměstnání</label>
									</div>
									<div
										className={
											'border border-1 rounded px-3 d-inline-block radio-cont' +
											(!formValues.actualJob ? ' highlight-border' : '')
										}
										/* eslint-disable-next-line react/jsx-no-bind */
										onClick={() =>
											onChange({
												target: {
													name: 'actualJob',
													value: false
												}
											})
										}>
										<input
											type="radio"
											className="form-check-input me-1"
											checked={!formValues.actualJob}
											readOnly={true}
										/>
										<label className="control-label">Bývalé zaměstnání</label>
									</div>
								</div>
							</div>
							<div className="d-none col-md-6"></div>
						</div>
						<div className="form-row row mt-5">
							<div className="col-md-4 form-group required">
								<label
									className="h5 control-label"
									ref={localityRef as unknown as LegacyRef<HTMLLabelElement>}
									htmlFor={'locality-select'}>
									Lokalita
								</label>
								<SuperSelect
									originalInputId={null}
									defaultValue={''}
									inputConfig={{
										className: 'form-control' + (isLocalityError() ? ' is-invalid' : ''),
										placeholder: 'např. Liberec',
										autoComplete: 'off',
										id: 'locality-select'
									}}
									wrapperConfig={{
										className: isLocalityError() ? ' is-invalid' : ''
									}}
									onSearch={locationOnSearch}
									/* eslint-disable-next-line react/jsx-no-bind */
									formatItem={(item, defaultOnClick) => (
										<a
											className="dropdown-item"
											/* eslint-disable-next-line react/jsx-no-bind */
											onClick={() => {
												onChange({
													target: {
														name: 'localityId',
														value: item.id
													}
												});

												defaultOnClick(item.text);
											}}>
											{item.text}
										</a>
									)}
								/>
								{isLocalityError() ? (
									<span className={'invalid-feedback'}>Vyberte lokalitu z nabídky</span>
								) : null}
							</div>
							<div className="col-md-4 mt-4 mt-md-0 form-group required">
								{departments.length > 0 ? (
									<>
										<label
											className="h5 control-label"
											ref={departmentRef as unknown as LegacyRef<HTMLLabelElement>}
											htmlFor={'department-select'}>
											Oddělení
										</label>
										<SuperSelect
											originalInputId={null}
											defaultValue={formValues.departmentName}
											initialList={departments}
											inputConfig={{
												className: 'form-control' + (isDepartmentError() ? ' is-invalid' : ''),
												placeholder: 'např. Administrativa',
												autoComplete: 'off',
												id: 'department-select'
											}}
											wrapperConfig={{
												className: isDepartmentError() ? ' is-invalid' : ''
											}}
											onSearch={departmentOnSearch}
											/* eslint-disable-next-line react/jsx-no-bind */
											formatItem={(item, defaultOnClick) =>
												item.name === 'divider_tag' ? (
													<SuperSelectDivider />
												) : (
													<a
														className="dropdown-item"
														/* eslint-disable-next-line react/jsx-no-bind */
														onClick={() => {
															setForceDepartmentError(false);
															setFormValues({
																...formValues,
																...{
																	departmentId: item.isDefault ? null : item.id,
																	departmentName: item.name
																}
															});

															defaultOnClick(item.name);
														}}>
														{item.name}
													</a>
												)
											}
										/>
										{isDepartmentError() ? (
											<span className={'invalid-feedback'}>Vyberte oddělení z nabídky</span>
										) : null}
									</>
								) : null}
							</div>
						</div>
					</div>
				</div>
			</section>
			<section className="rating-form">
				<div className="container-lg container-lg-max pt-5 pb-4">
					<h3
						className={errors.ratings ? 'is-invalid' : ''}
						ref={ratingsRef as unknown as LegacyRef<HTMLHeadingElement>}>
						Hodnocení hvězdičkou
					</h3>
					{errors.ratings ? <span className={'invalid-feedback'}>Vyplňte všechna hodnocení</span> : null}

					<div className="row pb-6">
						<div className="col-md-8">
							{ratingData.map(
								(group: IRatingGroup, groupKey: number): React.ReactNode => (
									<div
										className={
											'rating-hives d-flex flex-column mt-3 mb-5 pb-3 border border-0 border-bottom required form-group'
										}
										key={groupKey}>
										<label className={'control-label h4'}>{group.name}</label>
										{group.items.map(
											(item: IItem, key: number): React.ReactNode => (
												<div className="row mt-3" key={key}>
													<div className="col-12 col-sm-5 mb-2 mb-sm-0 fw-bold">
														{item.name}
													</div>
													<div className="col-12 col-sm-7 stars-cont">
														<Stars
															defaultValue={getRatingValue(item.id)}
															/* eslint-disable-next-line react/jsx-no-bind */
															onChange={(rating: number) =>
																onChange({
																	target: {
																		name: 'rating',
																		value: rating,
																		id: item.id
																	}
																})
															}
														/>
													</div>
												</div>
											)
										)}
									</div>
								)
							)}
						</div>
						<div className="col-md-4 mt-7 mt-md-0">
							<div className="rating-sum-box border border-1 rounded-4 px-4 py-4 d-flex flex-column align-items-center me-md-0 ms-md-auto">
								<h2 className="h4 mb-1">Vaše celkové hodnocení</h2>
								<h2 className="h3">
									{formValues.ratings.length === 0 ? 'Bez hodnocení' : getAverageRatingValue(false)}
								</h2>
								<div className="stars-cont">
									<Stars defaultValue={getAverageRatingValue()} readonly={true} />
								</div>
								<div className="number-notice mt-3 h5">
									<span className="filled text-black">{formValues.ratings.length}</span>/
									<span
										className={
											'sum' +
											(formValues.ratings.length === getRatingItemsCount() ? ' text-black' : '')
										}>
										{getRatingItemsCount()} položek
									</span>
								</div>
								<div className="text-center">
									Označte všechny položky hvězdičkami, ať je hodnocení co nejpřesnější.
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
			<section className="company-form">
				<div className="container-lg container-lg-max">
					<div className="mt-5 mb-4">
						<h3>Více o firmě</h3>
						<p>
							Přínosné recenze se řídí naším{' '}
							<a href={codexUrl} target={'_blank'} rel="noreferrer">
								desaterem zásad pro hodnocení firem
							</a>
						</p>
						<div className="form-row mt-3">
							<div className="form-group">
								<label
									className="h5 control-label smile-cont happy-icon-container mb-2"
									htmlFor={'ratingTextPositive'}>
									Co ve firmě hodnotím pozitivně
								</label>

								<TextArea
									id={'ratingTextPositive'}
									name={'ratingTextPositive'}
									onChange={onTextChange}
									value={formValues.ratingTextPositive}
									className="form-control"
									required={true}
									showCounter={true}
									maxLength={1000}
									rows={3}
									placeholder={
										'Např. skvělá atmosféra, vnímavý šéf, možnost kariérního postupu, rozmanitá práce, příjemné pracovní prostředí, aj.'
									}
								/>
							</div>
						</div>
						<div className="form-row mt-3">
							<div className="form-group">
								<label
									className="h5 control-label smile-cont sad-icon-container mb-2"
									htmlFor={'ratingTextNegative'}>
									Co ve firmě hodnotím negativně
								</label>
								<TextArea
									id={'ratingTextNegative'}
									name={'ratingTextNegative'}
									onChange={onTextChange}
									value={formValues.ratingTextNegative}
									className="form-control"
									required={true}
									showCounter={true}
									maxLength={1000}
									rows={3}
									placeholder={
										'Např. nedostatek uznání, neférový přístup, časté a/nebo neplacené přesčasy, pozdní výplaty, aj.'
									}
								/>
							</div>
						</div>
						<div className="form-row mt-3">
							<div className="form-group">
								<label
									className="h5 control-label smile-cont improve-icon-container mb-2"
									htmlFor={'ratingTextImprovement'}>
									Co by firma mohla ještě zlepšit
								</label>
								<TextArea
									id={'ratingTextImprovement'}
									name={'ratingTextImprovement'}
									onChange={onTextChange}
									value={formValues.ratingTextImprovement}
									className="form-control"
									required={true}
									showCounter={true}
									maxLength={1000}
									rows={3}
									placeholder={
										'Např. více benefitů, pravidelnější vzdělávání, možnost plánování směn, práce na homeoffice, více firemních akcí, aj.'
									}
								/>
							</div>
						</div>
						<div className="mt-4">
							<h4 className="h5">Jaké benefity firma nabízí</h4>
							<div className="row w-100">{renderBenefits()}</div>
						</div>
					</div>
				</div>
			</section>
			<section className="bottom-form">
				<div className="container-lg container-lg-max pt-5 pb-5">
					<h3>Na závěr</h3>
					<div className={'form-row mt-5 recommendation required form-group'}>
						<label
							className={'h5 control-label'}
							ref={recommendedRef as unknown as LegacyRef<HTMLLabelElement>}>
							Doporučili byste tuto firmu uchazečům o práci?
						</label>
						<div className={'d-flex flex-wrap row-gap-3' + (errors.recommended ? ' is-invalid mb-3 ' : '')}>
							<div className="d-flex">
								<div
									className={
										'border border-1 rounded px-3 py-2 me-3 d-inline-block inch-cont very-positive positive text-nowrap' +
										(formValues.recommended === 1 ? ' active' : '')
									}
									/* eslint-disable-next-line react/jsx-no-bind */
									onClick={() =>
										onChange({
											target: {
												name: 'recommended',
												value: 1
											}
										})
									}>
									<span>Určitě ano</span>
								</div>
								<div
									className={
										'border border-1 rounded px-3 py-2 me-3 d-inline-block inch-cont positive text-nowrap' +
										(formValues.recommended === 2 ? ' active' : '')
									}
									/* eslint-disable-next-line react/jsx-no-bind */
									onClick={() =>
										onChange({
											target: {
												name: 'recommended',
												value: 2
											}
										})
									}>
									<span>Spíše ano</span>
								</div>
							</div>
							<div className="d-flex">
								<div
									className={
										'border border-1 rounded px-3 py-2 me-3 d-inline-block inch-cont negative text-nowrap' +
										(formValues.recommended === 3 ? ' active' : '')
									}
									/* eslint-disable-next-line react/jsx-no-bind */
									onClick={() =>
										onChange({
											target: {
												name: 'recommended',
												value: 3
											}
										})
									}>
									<span>Spíše ne</span>
								</div>
								<div
									className={
										'border border-1 rounded px-3 py-2 me-3 d-inline-block inch-cont very-negative negative text-nowrap' +
										(formValues.recommended === 4 ? ' active' : '')
									}
									/* eslint-disable-next-line react/jsx-no-bind */
									onClick={() =>
										onChange({
											target: {
												name: 'recommended',
												value: 4
											}
										})
									}>
									<span>Rozhodně ne</span>
								</div>
							</div>
						</div>
						{errors.recommended ? <span className={'invalid-feedback'}>Vyplňte doporučení</span> : null}
					</div>

					<div className="form-row mt-5">
						<div className="col form-group required">
							<label className="h5 control-label">Váš e-mail (nebude zveřejněn)</label>
							<input
								name={'email'}
								type={'email'}
								className={'form-control'}
								autoComplete={'off'}
								value={formValues.email}
								disabled={true}
							/>
						</div>
					</div>
					<div className="form-row mt-5">
						<p>
							Odesláním hodnocení souhlasíte se&nbsp;
							<a href={'/zasady-ochrany-osobnich-udaju'} target={'_blank'} rel="noreferrer">
								Zásadami ochrany osobních údajů
							</a>
							&nbsp;a&nbsp;
							<a href={'/obchodni-podminky-pro-uzivatele'} target={'_blank'} rel="noreferrer">
								Obchodními podmínkami
							</a>
							&nbsp;pro uživatele portálu JenFirmy.cz a JenPráce.cz a&nbsp;
							<a href={'/kodex'} target={'_blank'} rel="noreferrer">
								potvrzujete, že respektujete pravidla
							</a>
							&nbsp;hodnocení JenFirmy.cz
						</p>
					</div>
					<div className="form-row form-check mt-5">
						<input
							className="form-check-input"
							type="checkbox"
							checked={formValues.gdprAgreement}
							id="gdprAgreement"
							/* eslint-disable-next-line react/jsx-no-bind */
							onChange={() =>
								onChange({
									target: {
										name: 'gdprAgreement',
										value: !formValues.gdprAgreement
									}
								})
							}
						/>
						<label className="form-check-label" htmlFor="gdprAgreement">
							Nechte si posílat nabídky práce na míru! Zaškrtnutím souhlasíte se zasíláním nabídek
							odpovídajících vašim preferencím. Souhlas lze kdykoliv odvolat. Více{' '}
							<a
								href={
									'https://www.jenprace.cz/data/download/informace-o-zpracovani-osobnich-udaju-jf-1.pdf'
								}
								target={'_blank'}
								rel="noreferrer">
								ZDE
							</a>
							.
						</label>
					</div>
					<div className="form-row mt-5">
						<Submit text={'Odeslat hodnocení'} className={'btn btn-primary btn-xl'} onClick={sendForm} />
					</div>
				</div>
			</section>
		</>
	);
};
